import type { ComponentType } from 'react';
import { CheckCircle2, Clock, XCircle, TrendingUp, MinusCircle } from 'lucide-react';

// The instructor's one specific complaint after the first real class: status
// was an oval, colour-only pill. This is the replacement everywhere on the
// record — a modern rounded RECTANGLE (never rounded-full), and state is
// always readable from the icon shape too, not colour alone (each tone gets
// a distinct glyph below).

export type TagTone = 'success' | 'warning' | 'danger' | 'accent' | 'neutral';
export type TagSize = 'sm' | 'md';

type IconType = ComponentType<{ size?: number; className?: string }>;

const TONE_CLASS: Record<TagTone, string> = {
  success: 'bg-ws-success-quiet text-ws-success',
  warning: 'bg-ws-gold-quiet text-ws-gold-ink',
  danger: 'bg-ws-danger/10 text-ws-danger',
  accent: 'bg-ws-accent-quiet text-ws-accent-ink',
  neutral: 'bg-ws-surface-2 text-ws-ink-3',
};

const TONE_ICON: Record<TagTone, IconType> = {
  success: CheckCircle2,
  warning: Clock,
  danger: XCircle,
  accent: TrendingUp,
  neutral: MinusCircle,
};

const SIZE_CLASS: Record<TagSize, string> = {
  sm: 'gap-1 rounded-md px-2 py-0.5 text-[11px]',
  md: 'gap-1.5 rounded-md px-2.5 py-1 text-xs',
};

export default function StatusTag({
  label, tone, size = 'md', icon,
}: {
  label: string;
  tone: TagTone;
  size?: TagSize;
  /** Override the tone's default glyph (rare — most callers should let the
   * tone pick it, so tone and shape never drift apart). */
  icon?: IconType;
}) {
  const Icon = icon || TONE_ICON[tone];
  return (
    <span className={`inline-flex items-center font-semibold ${SIZE_CLASS[size]} ${TONE_CLASS[tone]}`}>
      <Icon size={size === 'sm' ? 11 : 13} className="shrink-0" />
      {label}
    </span>
  );
}
